// ============================================================================
// ContactPicker.tsx — УНИВЕРСАЛЬНАЯ форма выбора контактов (SSoT/SRP).
// Используется в «Новом чате» (мультивыбор участников) и «Пригласить
// участника» (одиночный выбор). Особенности:
//   • поиск по имени/логину/email (регистронезависимо, мгновенно);
//   • карточки контактов: аватар (UserAvatar — SSoT аватаров), имя,
//     логин/email-подпись, состояние выбора (галочка/подсветка);
//   • адаптив: мобильный — крупные тач-карточки, desktop — сетка;
//   • мультивыбор (чипы выбранных сверху) / одиночный (выбор = click);
//   • «не выбран» / пустой список — явная подсказка (нет фоллбэков).
// Данные: контакты передаёт родитель (listEntities — SSoT сервера);
// фильтры (исключить себя/уже-участников) — тоже пропсами.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { Search, Users } from 'lucide-react';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { filterPeople } from '@/lib/people';

export interface Contact {
  id: string;
  name: string;
  avatarUrl?: string;
  /** подпись под именем (логин/email) */
  subtitle?: string;
  /** логин (по нему сервер разбирает упоминания «@login») */
  login?: string;
  /** признак бота/личности (entities.kind) — показываем бейдж */
  kind?: string;
}

interface ContactPickerProps {
  contacts: Contact[];
  /** исключить эти id из списка (себя, уже-участников) */
  excludeIds?: string[];
  /** выбранные id (контролируемый список) */
  selected?: string[];
  onSelect: (ids: string[]) => void;
  /** одиночный выбор (для «Пригласить») — вместо мультивыбора */
  single?: boolean;
  /** подпись-подсказка (пусто = авто) */
  placeholder?: string;
  /** максимум выбранных (для мультивыбора) */
  max?: number;
  /** высота списка (мобайл — больше) */
  listClassName?: string;
  /** доп. классы корня (напр. flex-1 min-h-0 — чтобы список скроллился, а не форма) */
  className?: string;
}

export function ContactPicker({
  contacts,
  excludeIds = [],
  selected = [],
  onSelect,
  single = false,
  placeholder,
  max = Infinity,
  listClassName = '',
  className = '',
}: ContactPickerProps) {
  const { isMobile } = useBreakpoint();
  const [query, setQuery] = useState('');

  // Нормализация подписи: логин/email
  const visible = useMemo(() => {
    const ex = new Set(excludeIds);
    const q = query.trim().toLowerCase();
    return filterPeople(contacts)
      .filter((c) => !ex.has(c.id))
      .filter((c) => {
        if (!q) return true;
        return (c.name || '').toLowerCase().includes(q)
          || (c.subtitle || '').toLowerCase().includes(q);
      });
  }, [contacts, excludeIds, query]);

  const toggle = (id: string) => {
    if (single) {
      onSelect([id]);
      return;
    }
    const cur = selected.includes(id);
    if (cur) {
      onSelect(selected.filter((x) => x !== id));
    } else {
      if (selected.length >= max) return;
      onSelect([...selected, id]);
    }
  };

  const selectedSet = new Set(selected);
  const selectedObjs = contacts.filter((c) => selectedSet.has(c.id));

  // Автосброс поиска при смене пропов-списка
  useEffect(() => { setQuery(''); }, [contacts.length]);

  return (
    <div className={`flex flex-col gap-2 min-h-0 ${className}`}>
      {/* Поиск */}
      <div className="relative shrink-0">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder || 'Поиск по имени, логину, email…'}
          className="w-full h-8 rounded-full border bg-muted/30 pl-8 pr-3 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary/40 placeholder:text-muted-foreground/70"
        />
      </div>

      {/* Выбранные (мультивыбор) — чипы сверху */}
      {!single && selectedObjs.length > 0 && (
        <div className="flex flex-wrap gap-1 shrink-0">
          {selectedObjs.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border border-primary/30 bg-primary/10 text-primary"
            >
              {c.name}
              <button
                className="hover:text-foreground leading-none"
                onClick={() => toggle(c.id)}
                title="Убрать"
                aria-label={`Убрать ${c.name}`}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Список контактов — карточки */}
      <div className={`overflow-auto min-h-0 ${listClassName || (isMobile ? 'max-h-64' : 'max-h-56')}`}>
        {visible.length === 0 ? (
          <div className="flex flex-col items-center gap-1 py-6 text-muted-foreground">
            <Users className="h-5 w-5 opacity-40" />
            <span className="text-[11px]">{query.trim() ? 'Никого не найдено' : 'Нет доступных контактов'}</span>
          </div>
        ) : (
          <div className={`grid gap-1 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {visible.map((c) => {
              const on = selectedSet.has(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => toggle(c.id)}
                  className={`group flex items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition-all ${
                    on
                      ? 'border-primary/50 bg-primary/10 shadow-sm'
                      : 'border-border/60 hover:border-primary/30 hover:bg-accent/40'
                  }`}
                >
                  <UserAvatar uid={c.id} fileId={c.avatarUrl} name={c.name} size={isMobile ? 38 : 34} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12px] font-medium truncate leading-tight">
                      {c.name}
                      {c.kind && (
                        <span className="ml-1 inline-block align-middle text-[8px] uppercase tracking-wide text-primary border border-primary/30 rounded px-1">
                          {c.kind}
                        </span>
                      )}
                    </span>
                    {c.subtitle && (
                      <span className="block text-[10px] text-muted-foreground truncate">{c.subtitle}</span>
                    )}
                  </span>
                  {/* Индикатор выбора */}
                  <span
                    className={`shrink-0 h-5 w-5 rounded-full border flex items-center justify-center text-[11px] transition-all ${
                      on ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/30 text-transparent group-hover:border-muted-foreground/60'
                    }`}
                  >
                    ✓
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
